import { useCallback, useEffect, useId, useState } from 'react'
import { useSiteState } from 'siteState/shared'
import useCancelModal from './CancelModal/useCancelModal'
import { Field, YesObject } from '@yes.technology/react-toolkit'
import { useBlocker } from 'site/shared/hooks/useBlocker'

type Callbacks = {
  onSucceed?: () => void
  onCancel?: () => void
}

type UseEditValuesProps = {
  fields: Pick<Field, 'column_json'>[]
  yesObject?: YesObject
  onSave?: (values: YesObject) => Promise<void>
}

export const REDIRECT_CALLBACK_KEY = 'REDIRECT_CALLBACK_KEY'
export const OBJECT_VIEW_NAVIGATION_CALLBACK_KEY = 'OBJECT_VIEW_NAVIGATION'

const pickRelatedFieldValues = (
  fields: Pick<Field, 'column_json'>[],
  yesObject?: YesObject
): YesObject | undefined => {
  if (!yesObject) {
    return yesObject
  }

  return Object.fromEntries(
    fields.flatMap((field) =>
      field.column_json in yesObject
        ? [[field.column_json, yesObject[field.column_json]]]
        : []
    )
  ) as YesObject
}

export default function useEditValues({
  fields,
  yesObject,
  onSave
}: UseEditValuesProps) {
  const id = useId()

  const [isEditing, setIsEditing] = useState(false)
  const [fieldValues, setFieldValues] = useState(() =>
    pickRelatedFieldValues(fields, yesObject)
  )
  const [editCallbacks, setEditCallbacks] = useSiteState<
    Record<string, Callbacks>
  >('cancel-modal-callbacks')

  const { register, blocker } = useBlocker()

  useEffect(
    () => register(() => !!Object.keys(editCallbacks).length),
    [editCallbacks, register]
  )

  const { open: openModal } = useCancelModal()

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setEditCallbacks((prevCallbacks) => ({
        ...prevCallbacks,
        [REDIRECT_CALLBACK_KEY]: {
          onSucceed: blocker.proceed,
          onCancel: blocker.reset
        }
      }))

      openModal()
    }
  }, [blocker, openModal, setEditCallbacks])

  const cancelChanges = useCallback(() => {
    setFieldValues(pickRelatedFieldValues(fields, yesObject))
    setIsEditing(false)
  }, [setFieldValues, setIsEditing, fields, yesObject])

  const addOnSuccessCallback = useCallback(
    (id: string, callback: () => void) => {
      setEditCallbacks((prevCallbacks) => ({
        ...prevCallbacks,
        [id]: { onSucceed: callback }
      }))
    },
    [setEditCallbacks]
  )

  const edit = useCallback(() => {
    addOnSuccessCallback(id, cancelChanges)

    setIsEditing(true)
  }, [addOnSuccessCallback, setIsEditing, cancelChanges, id])

  const cancel = useCallback(() => {
    isEditing && openModal(id)
  }, [isEditing, openModal, id])

  const onChange = useCallback(
    (fieldColumn: string, value: YesObject[string]) => {
      setFieldValues(
        (previousFieldValues) =>
          ({
            ...previousFieldValues,
            [fieldColumn]: value
          }) as YesObject
      )
    },
    []
  )

  const persistChanges = useCallback(() => {
    onSave && fieldValues && onSave(fieldValues)

    setIsEditing(false)
  }, [onSave, fieldValues, setIsEditing])

  const saveChanges = useCallback(async () => {
    if (fieldValues) await onSave?.(fieldValues)
  }, [fieldValues, onSave])

  const changeAndSave = useCallback(
    async (fieldColumn: string, value: YesObject[string]) => {
      const newFieldValues = {
        ...fieldValues,
        [fieldColumn]: value
      } as YesObject

      setFieldValues(newFieldValues)
      await onSave?.(newFieldValues)
    },
    [fieldValues, onSave]
  )

  useEffect(() => {
    setFieldValues(pickRelatedFieldValues(fields, yesObject))
  }, [fields, yesObject])

  return {
    edit,
    cancel,
    onChange,
    addOnSuccessCallback,
    cancelChanges,
    persistChanges,
    saveChanges,
    changeAndSave,
    fieldValues,
    isEditing
  }
}
